import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Archive, Plus, RotateCcw, Users } from 'lucide-react'
import { supabase } from '../supabase'
import { useToast } from '../Toast'
import { useUser } from '../UserContext'
import ConfirmModal from '../ConfirmModal'

const EMPTY_WORKSPACE = { name: '', base_currency: 'GBP' }

export default function CrmWorkspaceSettings() {
  const toast = useToast()
  const toastRef = useRef(toast)
  toastRef.current = toast
  const { user, orgId, isAdmin, isPlatformAdmin, crmEnabled, refreshProfile } = useUser()
  const canManage = isAdmin || isPlatformAdmin
  const [workspaces, setWorkspaces] = useState([])
  const [profiles, setProfiles] = useState([])
  const [members, setMembers] = useState([])
  const [businesses, setBusinesses] = useState([])
  const [businessAccess, setBusinessAccess] = useState([])
  const [selectedId, setSelectedId] = useState(null)
  const [showArchived, setShowArchived] = useState(false)
  const [showCreate, setShowCreate] = useState(false)
  const [createForm, setCreateForm] = useState(EMPTY_WORKSPACE)
  const [editForm, setEditForm] = useState(EMPTY_WORKSPACE)
  const [newMemberId, setNewMemberId] = useState('')
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [archiveTarget, setArchiveTarget] = useState(null)

  const fetchWorkspaces = useCallback(async (preferredId = null) => {
    if (!orgId || !crmEnabled || !canManage) {
      setLoading(false)
      return
    }

    setLoading(true)
    const [{ data: workspaceData, error: workspaceError }, { data: profileData, error: profileError }] = await Promise.all([
      supabase.from('crm_workspaces').select('*').eq('org_id', orgId).order('archived').order('name'),
      supabase.from('profiles').select('id, full_name, email, role').eq('org_id', orgId).order('full_name')
    ])

    if (workspaceError || profileError) {
      toastRef.current.error('Failed to load CRM workspace settings')
      setLoading(false)
      return
    }

    const loaded = workspaceData || []
    setWorkspaces(loaded)
    setProfiles(profileData || [])
    setSelectedId(current => {
      if (preferredId && loaded.some(workspace => workspace.id === preferredId)) return preferredId
      if (loaded.some(workspace => workspace.id === current)) return current
      return loaded.find(workspace => !workspace.archived)?.id || loaded[0]?.id || null
    })
    setLoading(false)
  }, [canManage, crmEnabled, orgId])

  const fetchWorkspaceDetails = useCallback(async () => {
    if (!selectedId) {
      setMembers([])
      setBusinesses([])
      setBusinessAccess([])
      return
    }

    const [memberResult, businessResult, accessResult] = await Promise.all([
      supabase.from('crm_workspace_members').select('*').eq('workspace_id', selectedId),
      supabase.from('crm_businesses').select('id, name, archived').eq('workspace_id', selectedId).eq('archived', false).order('name'),
      supabase.from('crm_member_business_access').select('*').eq('workspace_id', selectedId)
    ])

    if (memberResult.error || businessResult.error || accessResult.error) {
      toastRef.current.error('Failed to load workspace access settings')
      return
    }
    setMembers(memberResult.data || [])
    setBusinesses(businessResult.data || [])
    setBusinessAccess(accessResult.data || [])
  }, [selectedId])

  useEffect(() => { fetchWorkspaces() }, [fetchWorkspaces])
  useEffect(() => { fetchWorkspaceDetails() }, [fetchWorkspaceDetails])

  const selected = workspaces.find(workspace => workspace.id === selectedId) || null

  useEffect(() => {
    if (selected) setEditForm({ name: selected.name, base_currency: selected.base_currency })
  }, [selected])

  useEffect(() => {
    if (!showArchived && selected?.archived) {
      setSelectedId(workspaces.find(workspace => !workspace.archived)?.id || null)
    }
  }, [selected, showArchived, workspaces])

  const visibleWorkspaces = workspaces.filter(workspace => showArchived || !workspace.archived)
  const availableProfiles = profiles.filter(profile => !members.some(member => member.user_id === profile.id))
  const activeWorkspaceCount = workspaces.filter(workspace => !workspace.archived).length

  async function createWorkspace() {
    if (!createForm.name.trim()) return toast.error('Workspace name is required')
    setSaving(true)
    const { data: workspaceId, error } = await supabase.rpc('create_crm_workspace', {
      workspace_name: createForm.name.trim(),
      workspace_base_currency: createForm.base_currency
    })

    if (error) {
      console.error('Failed to create CRM workspace:', error)
      setSaving(false)
      return toast.error(error.code === '23505'
        ? 'An active workspace already uses that name'
        : error.message || 'Failed to create workspace')
    }

    setSaving(false)

    toast.success('CRM workspace created')
    setCreateForm(EMPTY_WORKSPACE)
    setShowCreate(false)
    await fetchWorkspaces(workspaceId)
    await refreshProfile({ authUser: user })
  }

  async function updateWorkspace() {
    if (!selected || !editForm.name.trim()) return toast.error('Workspace name is required')
    setSaving(true)
    const { error } = await supabase.from('crm_workspaces').update({
      name: editForm.name.trim(),
      base_currency: editForm.base_currency,
      updated_at: new Date().toISOString()
    }).eq('id', selected.id)
    setSaving(false)
    if (error) return toast.error(error.code === '23505' ? 'An active workspace already uses that name' : 'Failed to update workspace')
    toast.success('Workspace updated')
    await fetchWorkspaces(selected.id)
    await refreshProfile({ authUser: user })
  }

  async function setArchived(workspace, archived) {
    if (archived && activeWorkspaceCount <= 1) {
      setArchiveTarget(null)
      return toast.error('At least one active CRM workspace is required')
    }
    const { error } = await supabase.from('crm_workspaces').update({ archived, updated_at: new Date().toISOString() }).eq('id', workspace.id)
    setArchiveTarget(null)
    if (error) return toast.error(`Failed to ${archived ? 'archive' : 'restore'} workspace`)
    toast.success(archived ? 'Workspace archived' : 'Workspace restored')
    const nextWorkspaceId = archived
      ? workspaces.find(item => !item.archived && item.id !== workspace.id)?.id || null
      : workspace.id
    await fetchWorkspaces(nextWorkspaceId)
    await refreshProfile({ authUser: user })
  }

  async function addMember() {
    if (!newMemberId || !selected) return
    const profile = profiles.find(item => item.id === newMemberId)
    const { error } = await supabase.from('crm_workspace_members').insert({
      workspace_id: selected.id,
      user_id: newMemberId,
      role: profile?.role === 'admin' ? 'workspace_admin' : 'member',
      visibility_mode: 'all'
    })
    if (error) return toast.error('Failed to add workspace member')
    toast.success('Workspace member added')
    setNewMemberId('')
    fetchWorkspaceDetails()
  }

  async function updateMember(member, updates) {
    const { error } = await supabase.from('crm_workspace_members').update(updates)
      .eq('workspace_id', member.workspace_id).eq('user_id', member.user_id)
    if (error) return toast.error('Failed to update workspace access')
    setMembers(current => current.map(item => item.user_id === member.user_id ? { ...item, ...updates } : item))
    toast.success('Workspace access updated')
  }

  async function removeMember(member) {
    const { error } = await supabase.from('crm_workspace_members').delete()
      .eq('workspace_id', member.workspace_id).eq('user_id', member.user_id)
    if (error) return toast.error('Failed to remove workspace member')
    toast.success('Workspace member removed')
    fetchWorkspaceDetails()
  }

  async function toggleBusiness(member, businessId) {
    const existing = businessAccess.find(access => access.user_id === member.user_id && access.business_id === businessId)
    const query = existing
      ? supabase.from('crm_member_business_access').delete().eq('workspace_id', selected.id).eq('user_id', member.user_id).eq('business_id', businessId)
      : supabase.from('crm_member_business_access').insert({ workspace_id: selected.id, user_id: member.user_id, business_id: businessId })
    const { error } = await query
    if (error) return toast.error('Failed to update Business access')
    fetchWorkspaceDetails()
  }

  if (!crmEnabled || !canManage) return null

  return (
    <section className="hv-card crm-workspace-settings">
      <div className="crm-settings-heading">
        <div>
          <h2>CRM Workspaces</h2>
          <p>Separate CRM teams, reporting currencies and record access.</p>
        </div>
        <button className="hv-btn hv-btn-primary" onClick={() => setShowCreate(value => !value)}>
          <Plus size={15} /> New Workspace
        </button>
      </div>

      {showCreate && (
        <div className="crm-workspace-create">
          <div><label className="hv-form-label">Workspace Name</label><input className="hv-input" autoFocus value={createForm.name} onChange={event => setCreateForm({ ...createForm, name: event.target.value })} placeholder="e.g. UK Partnerships" /></div>
          <div><label className="hv-form-label">Base Currency</label><CurrencySelect value={createForm.base_currency} onChange={base_currency => setCreateForm({ ...createForm, base_currency })} /></div>
          <button className="hv-btn hv-btn-primary" disabled={saving} onClick={createWorkspace}>{saving ? 'Creating...' : 'Create Workspace'}</button>
        </div>
      )}

      <label className="crm-archived-toggle"><input type="checkbox" checked={showArchived} onChange={event => setShowArchived(event.target.checked)} /> Show archived workspaces</label>

      {loading ? <div className="hv-empty-text">Loading workspaces...</div> : (
        <div className="crm-settings-layout">
          <div className="crm-workspace-list">
            {visibleWorkspaces.map(workspace => (
              <button key={workspace.id} className={workspace.id === selectedId ? 'active' : ''} onClick={() => setSelectedId(workspace.id)}>
                <span>{workspace.name}</span><small>{workspace.base_currency}{workspace.archived ? ' · Archived' : ''}</small>
              </button>
            ))}
          </div>

          {selected && (
            <div className="crm-workspace-detail">
              <div className="crm-workspace-fields">
                <div><label className="hv-form-label">Workspace Name</label><input className="hv-input" disabled={selected.archived} value={editForm.name} onChange={event => setEditForm({ ...editForm, name: event.target.value })} /></div>
                <div><label className="hv-form-label">Base Currency</label><CurrencySelect disabled={selected.archived} value={editForm.base_currency} onChange={base_currency => setEditForm({ ...editForm, base_currency })} /></div>
                {!selected.archived && <button className="hv-btn hv-btn-primary" disabled={saving} onClick={updateWorkspace}>Save Changes</button>}
                {selected.archived
                  ? <button className="hv-btn hv-btn-secondary" onClick={() => setArchived(selected, false)}><RotateCcw size={14} /> Restore</button>
                  : <button className="hv-btn hv-btn-danger" onClick={() => setArchiveTarget(selected)}><Archive size={14} /> Archive</button>}
              </div>

              {!selected.archived && <MemberSettings profiles={profiles} members={members} businesses={businesses} businessAccess={businessAccess} availableProfiles={availableProfiles} newMemberId={newMemberId} setNewMemberId={setNewMemberId} addMember={addMember} updateMember={updateMember} removeMember={removeMember} toggleBusiness={toggleBusiness} />}
            </div>
          )}
        </div>
      )}

      {archiveTarget && <ConfirmModal title="Archive CRM workspace" message={`Archive “${archiveTarget.name}”? Its records will remain stored, but the workspace will disappear from the CRM selector.`} confirmLabel="Archive Workspace" onConfirm={() => setArchived(archiveTarget, true)} onCancel={() => setArchiveTarget(null)} />}
    </section>
  )
}

function MemberSettings({ profiles, members, businesses, businessAccess, availableProfiles, newMemberId, setNewMemberId, addMember, updateMember, removeMember, toggleBusiness }) {
  const rows = useMemo(() => members.map(member => ({ ...member, profile: profiles.find(profile => profile.id === member.user_id) })), [members, profiles])
  return <div className="crm-member-settings">
    <div className="crm-member-heading"><div><Users size={16} /><strong>Workspace Members</strong></div><div><select className="hv-select" value={newMemberId} onChange={event => setNewMemberId(event.target.value)}><option value="">Select team member</option>{availableProfiles.map(profile => <option key={profile.id} value={profile.id}>{profile.full_name || profile.email}</option>)}</select><button className="hv-btn hv-btn-secondary" disabled={!newMemberId} onClick={addMember}>Add</button></div></div>
    {rows.length === 0 ? <div className="hv-empty-text">No members are assigned to this workspace.</div> : rows.map(member => <div className="crm-member-row" key={member.user_id}>
      <div className="crm-member-identity"><strong>{member.profile?.full_name || member.profile?.email}</strong><span>{member.profile?.email}</span></div>
      <select className="hv-select" value={member.role} onChange={event => updateMember(member, event.target.value === 'workspace_admin' ? { role: 'workspace_admin', visibility_mode: 'all' } : { role: 'member' })}><option value="member">Member</option><option value="workspace_admin">Workspace Admin</option></select>
      <select className="hv-select" disabled={member.role === 'workspace_admin'} value={member.role === 'workspace_admin' ? 'all' : member.visibility_mode} onChange={event => updateMember(member, { visibility_mode: event.target.value })}><option value="all">All records</option><option value="owned">Owned records</option><option value="selected_businesses">Selected Businesses</option></select>
      <button className="hv-btn hv-btn-danger" onClick={() => removeMember(member)}>Remove</button>
      {member.visibility_mode === 'selected_businesses' && <div className="crm-business-access">{businesses.length === 0 ? <span>No Businesses exist in this workspace yet.</span> : businesses.map(business => <label key={business.id}><input type="checkbox" checked={businessAccess.some(access => access.user_id === member.user_id && access.business_id === business.id)} onChange={() => toggleBusiness(member, business.id)} /> {business.name}</label>)}</div>}
    </div>)}
  </div>
}

function CurrencySelect({ value, onChange, disabled = false }) {
  return <select className="hv-select" disabled={disabled} value={value} onChange={event => onChange(event.target.value)}>{['GBP', 'EUR', 'USD'].map(currency => <option key={currency} value={currency}>{currency}</option>)}</select>
}
